// Your agent's suggestions at the top of the Inbox drawer (M4-03; 00 §4 "inbox triage, drafting
// replies"): each pending proposal of your crew ("Your agent suggests: Done / Snooze until … /
// Reply: '…' / Remind / Task update") with the item it is about, its reason, and Accept or
// Dismiss. A reply shows the drafted text. Only with the `agents` add-on, for a person (an agency
// operator has no crew), and only when there is something to decide: no proposals, no block.
// Accepting applies it (the item leaves the inbox, the reply is posted), so the inbox refetches.
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, Notice } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SparklesIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { crewKeys, crewProposalsQuery } from "../queries";
import { problemText } from "../settings/problem-text";
import { featureState, useSession } from "../use-feature";
import { invalidateWork } from "../work/work-queries";

type Proposal = OperationOutput<"crews.acceptProposal">;

const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
const quote = (s: string, n = 280) => `“${s.length > n ? `${s.slice(0, n - 1)}…` : s}”`;

/** "Done", "Snooze until Tue 21 Oct, 09:00", "Reply", "Remind", "Task update". */
export function suggestionTitle(p: Proposal): string {
  switch (p.kind) {
    case "done":
      return "Done";
    case "snooze":
      return `Snooze until ${when(p.snooze_until)}`;
    case "reply":
      return "Reply";
    case "remind":
      return `Remind ${when(p.remind_at)}`;
    case "task_update":
      return p.payload.status ? `Task update: ${p.payload.status}` : "Task update";
  }
}

/** Whether this person has a crew in the company with the add-on on. */
export function useHasCrew(company: CompanyAccess): boolean {
  const { me } = useSession();
  return (
    featureState(me, company.id, "agents").enabled &&
    me.memberships.some((m) => m.company_id === company.id && m.kind === "person")
  );
}

export function CrewSuggestions({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const enabled = useHasCrew(company);
  const proposals = useQuery({ ...crewProposalsQuery(api, co), enabled });
  const settle = () => {
    void qc.invalidateQueries({ queryKey: crewKeys.proposals(co) });
    void invalidateWork(qc, co);
  };
  const decide = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      accept
        ? api.client.crews.acceptProposal({ params: { co, id } })
        : api.client.crews.dismissProposal({ params: { co, id } }),
    onSettled: settle,
  });
  const rows = enabled ? (proposals.data?.data ?? []) : [];
  if (rows.length === 0 && !decide.error) return null;
  return (
    <section
      aria-label="Your agent suggests"
      data-slot="crew-suggestions"
      className="flex flex-col gap-2 border-b border-subtle p-3"
    >
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase">
        <SparklesIcon aria-hidden="true" className="size-3.5" />
        Your agent suggests
      </h3>
      <ul className="flex flex-col gap-2">
        {rows.map((p) => (
          <li
            key={p.id}
            data-proposal={p.id}
            data-kind={p.kind}
            className="flex flex-col gap-1.5 rounded-md border border-border bg-card p-3"
          >
            <div className="flex items-center gap-2">
              <Badge variant="info">{suggestionTitle(p)}</Badge>
              {p.item ? (
                <span className="min-w-0 flex-1 truncate text-sm">{p.item.summary}</span>
              ) : null}
            </div>
            {p.kind === "reply" && p.payload.body_markdown ? (
              <p className="text-sm whitespace-pre-wrap" data-slot="draft">
                {quote(p.payload.body_markdown, 600)}
              </p>
            ) : p.kind === "remind" && p.payload.text ? (
              <p className="text-sm">{quote(p.payload.text)}</p>
            ) : p.kind === "task_update" && p.payload.comment ? (
              <p className="text-sm">{quote(p.payload.comment)}</p>
            ) : null}
            {p.reason ? <p className="text-xs text-muted-foreground">{p.reason}</p> : null}
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={decide.isPending}
                aria-label={`Accept: ${suggestionTitle(p)}`}
                onClick={() => decide.mutate({ id: p.id, accept: true })}
              >
                Accept
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={decide.isPending}
                aria-label={`Dismiss: ${suggestionTitle(p)}`}
                onClick={() => decide.mutate({ id: p.id, accept: false })}
              >
                Dismiss
              </Button>
            </div>
          </li>
        ))}
      </ul>
      {decide.error ? (
        <Notice tone="danger" role="alert">
          {problemText(decide.error, "Your agent's suggestion could not be applied.")}
        </Notice>
      ) : null}
    </section>
  );
}
